import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Strong,
  Text,
} from '@radix-ui/themes';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonProgress } from 'components/common/progress';
import { MachineAutoFireButton } from 'components/machine/buttons/auto-fire';
import useBurnInTestStore from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in/use-burn-in-test-store';
import { AimingContext, AimingProvider } from 'contexts/aiming.context';
import { MachineButton } from 'contexts/firing/button';
import { FiringContext } from 'contexts/firing/context';
import { MachineContext } from 'contexts/machine.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { IgnoreAutoFireMode } from 'enums/firing';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import {
  LOCAL_DATETIME_FORMAT,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { QueueState, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { ITrainingMsg } from 'lib_ts/interfaces/machine-msg/i-training';
import { DEFAULT_PLATE, IPitch, IPitchList } from 'lib_ts/interfaces/pitches';
import { DEFAULT_SEAMS } from 'lib_ts/interfaces/pitches/i-base';
import { clone, first } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AdminBurnInTestsService } from 'services/admin/machine-burn-in-tests.service';
import { PitchListsService } from 'services/pitch-lists.service';
import { PitchesService } from 'services/pitches.service';
import { StateTransformService } from 'services/state-transform.service';
import useMachineStatusStore from 'stores/use-machine-status-store';

const COMPONENT_NAME = 'MachineBurnInFiringTest';

export const MachineBurnInFiringTestHoC = () => {
  return (
    <AimingProvider identifier="MachineBurnInFiringTestHoC" training>
      <MachineBurnInFiringTest />
    </AimingProvider>
  );
};

const MachineBurnInFiringTest = () => {
  const queueState = useMachineStatusStore((s) => s.queueState);

  const { lastFired, machine, setAutoFire, specialMsTarget } =
    useContext(MachineContext);

  // pause AF until a shot has been recorded > move onto the next pitch vs fire again
  const { safeSetIgnoreAf } = useContext(FiringContext);

  const { setAndSendForTraining } = useContext(AimingContext);

  const tests = useBurnInTestStore((s) => s.tests);
  const activeTestID = useBurnInTestStore((s) => s.activeTestID);

  const activeTest = useMemo(
    () => tests.find((m) => m._id === activeTestID),
    [tests, activeTestID]
  );

  const latestTest = useLatest(activeTest);

  const [refFiringList, setRefFiringList] = useState<IPitchList>();

  // find the first burn-in list, required to create new tests
  useEffect(() => {
    if (!activeTest?.firingTest?.listID) {
      return;
    }

    PitchListsService.getInstance()
      .getLiveReferences()
      .then((results) => {
        setRefFiringList(
          results.find((m) => m._id === activeTest.firingTest?.listID)
        );
      });
  }, [activeTest?.firingTest?.listID]);

  const [pitches, setPitches] = useState<IPitch[]>();

  const remainingPitches = useMemo(
    () => pitches?.filter((m) => !activeTest?.firingTest?.processed[m._id]),
    [activeTest?.firingTest?.processed, pitches]
  );

  const activePitch = first(remainingPitches);

  useEffect(() => {
    // by default, enable AF
    console.debug(`${COMPONENT_NAME}: mounted, enabling AF`);
    setAutoFire(true);
  }, []);

  // whenever a fire happens, temporarily ignore AF b/c we will need to wait for the shot > update the firingTest before proceeding
  useEffect(() => {
    console.debug(
      `${COMPONENT_NAME}: machine was fired, temporarily ignoring AF...`
    );
    safeSetIgnoreAf(IgnoreAutoFireMode.Temporary);
  }, [lastFired]);

  // load pitches from the reference list
  useEffect(() => {
    if (!activeTest?.firingTest?.listID) {
      return;
    }

    PitchesService.getInstance()
      .getList(activeTest.firingTest.listID)
      .then((result) => setPitches(result));
  }, [activeTest?.firingTest?.listID]);

  // build and send the active pitch
  useEffect(() => {
    if (queueState !== QueueState.Active) {
      console.debug(`${COMPONENT_NAME}: not active on machine`);
      return;
    }

    if (!activePitch) {
      console.debug(`${COMPONENT_NAME}: no active pitch`);
      return;
    }

    const callback = async () => {
      const builtMs = await StateTransformService.getInstance()
        .buildMany({
          machine: machine,
          notifyError: true,
          chars: [
            {
              build_id: activePitch._id,
              priority: activePitch.priority ?? BuildPriority.Default,
              bs: activePitch.bs,
              bs_hash: activePitch.bs_hash,
              traj: activePitch.traj,
              plate: activePitch.plate_loc_backup ?? DEFAULT_PLATE,
              seams: activePitch.seams ?? DEFAULT_SEAMS,
              breaks: activePitch.breaks,
            },
          ],
        })
        .then((buildResults) =>
          buildResults
            ? first(Object.values(buildResults))?.chars?.ms
            : undefined
        );

      if (!builtMs) {
        console.warn(`${COMPONENT_NAME}: failed to build ${activePitch.name}`);
        return;
      }

      await setAndSendForTraining({
        pitch: {
          ...activePitch,
          _mx_ms: builtMs,
        },
        sendConfig: {
          aimingShots: [],
          trigger: `${COMPONENT_NAME} > activePitch useEffect`,
        },
      });
    };

    callback();
  }, [machine, queueState, activePitch]);

  // listen for training responses and mark pitches as fired as shots come in
  useEffect(() => {
    if (!activeTestID) {
      return;
    }

    const callback = async (event: CustomEvent) => {
      const data = event.detail as ITrainingMsg;

      if (data.success === undefined) {
        // ignore interim training messages (e.g. when individual tracking devices report their own detections)
        return;
      }

      if (!data.shot) {
        // shot failed to be created, try again
        console.warn(`${COMPONENT_NAME}: shot failed to be created`);
        safeSetIgnoreAf(IgnoreAutoFireMode.Disabled);
        return;
      }

      if (!data.shot.pitch_id) {
        // shot failed to be created or has no pitch ID, try again
        console.warn(`${COMPONENT_NAME}: shot did not contain a pitch ID`);
        safeSetIgnoreAf(IgnoreAutoFireMode.Disabled);
        return;
      }

      if (!latestTest.current?.firingTest) {
        // no firing test defined, nothing to do
        return;
      }

      const nextFiringTest = clone(latestTest.current.firingTest);
      nextFiringTest.processed[data.shot.pitch_id] = new Date().toISOString();
      nextFiringTest.shots[data.shot.pitch_id] = data.shot._id;

      const updatedTest = await AdminBurnInTestsService.getInstance().putOne(
        activeTestID,
        { firingTest: nextFiringTest }
      );

      if (!updatedTest) {
        console.warn(`${COMPONENT_NAME}: failed to update test`);
        return;
      }

      console.debug(`${COMPONENT_NAME}: updating active test`, updatedTest);
      useBurnInTestStore.getState().updateTest(updatedTest);
    };

    WebSocketHelper.on(WsMsgType.M2U_TrainingResponse, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_TrainingResponse, callback);
    };
  }, [activeTestID]);

  // when there are no more remaining pitches, mark the test as completed and stop firing
  useEffect(() => {
    if (!remainingPitches) {
      // pitches haven't loaded yet, do nothing
      return;
    }

    if (remainingPitches.length > 0) {
      // there are still pitches remaining, do nothing
      return;
    }

    // immediately stop firing upon completing the test
    setAutoFire(false);

    if (!activeTestID) {
      console.debug(`${COMPONENT_NAME}: skipping end update, no active test`);
      return;
    }

    if (latestTest.current?.end) {
      console.debug(`${COMPONENT_NAME}: skipping end update, already ended`);
      return;
    }

    // mark the test as completed
    AdminBurnInTestsService.getInstance()
      .putOne(activeTestID, {
        end: new Date().toISOString(),
      })
      .then((updatedTest) => {
        if (!updatedTest) {
          console.warn(`${COMPONENT_NAME}: failed to update test`);
          return;
        }

        console.debug(`${COMPONENT_NAME}: updating active test`, updatedTest);
        useBurnInTestStore.getState().updateTest(updatedTest);
      });
  }, [activeTestID, remainingPitches]);

  // wait for pitches to load first
  const progress =
    pitches && remainingPitches && pitches.length > 0
      ? (pitches.length - remainingPitches.length) / pitches.length
      : 0;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonBackArrow
        onClick={() => useBurnInTestStore.setState({ activeTestID: null })}
      />

      <Card size="3">
        <Flex direction="column" gap="4">
          <Flex gap="2" justify="between" align="center">
            <Heading>Firing Test</Heading>

            {activeTest?.start && (
              <Badge>
                Start:{' '}
                {format(parseISO(activeTest.start), LOCAL_DATETIME_FORMAT, {
                  timeZone: LOCAL_TIMEZONE,
                })}
              </Badge>
            )}
          </Flex>

          <CommonCallout
            content={
              refFiringList ? (
                <Text>
                  The machine will <Strong>automatically fire</Strong> 1 shot
                  for each pitch in "{refFiringList.name}".
                  <br />
                  <Strong>
                    Please ensure the area is clear while the test is underway.
                  </Strong>
                </Text>
              ) : (
                <Text>Please wait...</Text>
              )
            }
          />

          <CommonProgress
            leftLabel={
              pitches && pitches.length > 0 ? (
                <Text>
                  Progress:{' '}
                  {activePitch ? (
                    <Badge>{activePitch.name}</Badge>
                  ) : (
                    <Badge color={RADIX.COLOR.SUCCESS}>Complete</Badge>
                  )}
                </Text>
              ) : (
                // pitches are loading
                <Text>Please wait...</Text>
              )
            }
            rightLabel={
              remainingPitches && (
                <Text>{remainingPitches.length} remaining</Text>
              )
            }
            value={progress * 100}
          />

          <Flex gap="2" align="center">
            <Box minWidth="120px">
              <Button
                className="btn-block"
                color={RADIX.COLOR.WARNING}
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                onClick={() => {
                  specialMsTarget(SpecialMsPosition.lowered);
                }}
              >
                {t('common.lower-machine')}
              </Button>
            </Box>

            <Box flexGrow="1">{/* placeholder */}</Box>

            {remainingPitches && remainingPitches.length > 0 && (
              <Box>
                <MachineAutoFireButton
                  beforeToggleFn={(newValue) =>
                    safeSetIgnoreAf(
                      newValue
                        ? IgnoreAutoFireMode.Temporary
                        : IgnoreAutoFireMode.Disabled
                    )
                  }
                />
              </Box>
            )}

            {remainingPitches && remainingPitches.length > 0 && (
              <Box minWidth="120px">
                <MachineButton
                  identifier="BurnInFiringTestMachineButton"
                  className="btn-block"
                  trainingShots={[]}
                  beforeFire={() => {
                    // will be lifted once a final training response arrives
                    safeSetIgnoreAf(IgnoreAutoFireMode.Temporary);
                  }}
                  fireData={{
                    mode: 'manual',
                    trigger: 'click',
                    training: true,
                    training_mode: undefined,
                    hitter_id: undefined,
                    tags: 'BURN IN',
                  }}
                  sendData={{
                    skipPreview: true,
                    trigger: COMPONENT_NAME,
                    aimingShots: [],
                  }}
                />
              </Box>
            )}
          </Flex>
        </Flex>
      </Card>
    </ErrorBoundary>
  );
};
